/**
 * Superadmin security (ADR-0036 Fase 2 — F2.0). Two surfaces:
 *  - the admin's OWN 2FA (the shared `TwoFactorPanel` over the platform TOTP adapter);
 *  - the realm-wide ENFORCEMENT toggle (turning it ON forces every admin without 2FA
 *    into setup at their next login).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, ShieldCheck } from "lucide-react";

import { TwoFactorPanel } from "@/components/auth/totp";
import { Button } from "@/components/ui/button";
import { PageContainer, PageContent, PageHeader } from "@/components/ui/page";
import { platformApi } from "@/lib/platform";
import { platformTotpAdapter } from "@/lib/platformTotp";

const POLICY_KEY = ["platform", "2fa", "policy"];

export function PlatformSecurityPage() {
  const qc = useQueryClient();
  const policyQ = useQuery({ queryKey: POLICY_KEY, queryFn: () => platformApi.getTotpPolicy() });
  const setPolicy = useMutation({
    mutationFn: (required: boolean) => platformApi.setTotpPolicy(required),
    onSuccess: (data) => qc.setQueryData(POLICY_KEY, data),
  });

  const required = policyQ.data?.required ?? false;

  return (
    <PageContainer>
      <PageHeader
        title="Sicurezza"
        description="Autenticazione a due fattori del realm superadmin."
      />
      <PageContent className="max-w-2xl space-y-6">
        {/* This admin's own 2FA — the shared panel, over the platform adapter. */}
        <section className="rounded-lg border border-border/60 bg-surface p-5">
          <TwoFactorPanel adapter={platformTotpAdapter} />
        </section>

        {/* Realm-wide enforcement. */}
        <section className="space-y-3 rounded-lg border border-border/60 bg-surface p-5">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
            <h3 className="text-sm font-medium">2FA obbligatoria per i superadmin</h3>
          </div>
          <p className="text-sm text-muted-foreground">
            Se attiva, ogni superadmin senza 2FA dovrà configurarla al prossimo accesso. Non potrai
            più disattivare la tua 2FA finché l'obbligo è attivo.
          </p>
          <div className="flex items-center gap-3">
            <Button
              size="sm"
              variant={required ? "danger" : "default"}
              disabled={policyQ.isLoading || setPolicy.isPending}
              onClick={() => setPolicy.mutate(!required)}
            >
              {setPolicy.isPending && (
                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
              )}
              {required ? "Disattiva obbligo" : "Rendi obbligatoria"}
            </Button>
            <span className="text-sm text-muted-foreground">
              Stato:{" "}
              <span className={required ? "font-medium text-warning" : "text-muted-foreground"}>
                {policyQ.isLoading ? "…" : required ? "obbligatoria" : "facoltativa"}
              </span>
            </span>
          </div>
        </section>
      </PageContent>
    </PageContainer>
  );
}
